import { useEffect, useMemo } from "react";
import { ConnectedProps, connect } from "react-redux";
import { useParams, useLocation } from "react-router-dom";
import { AppDispatch, RootState } from "store";
import { WrapperPreliquidation } from "./WrapperPreliquidation";
import {
  PagesPreliquidationTypes,
} from "./types";
import { preliquidationSelector, actions } from "reducers/preliquidation";
import { NotificationStateType } from "reducers/types/notification";
import { actions as notificationActions } from "reducers/notification";
import { actions as sessionActions, sessionSelector } from "reducers/session";
import { DEFAULT_COUNTRY } from "hooks/useSession";
import { BULK_FLOW_ID } from "utils/constants";
import { dirtyAlert } from "utils/alerts";
import { parseQueryParams } from "utils/utils";

export const WrapperPreliquidationContainer = (
  props:  ConnectorProps
): JSX.Element => {
  const params: { id?: string } = useParams();
  const location = useLocation();

  const queryParams = parseQueryParams(location.search);
  const tabFromUrl = queryParams.tab as PagesPreliquidationTypes;

  useEffect(() => {
    // Aprobacion masiva
    if (params.id === BULK_FLOW_ID) {
      props.setActualPage("invoice");
    } else {
      if (!tabFromUrl || (tabFromUrl !== "preliquidation" && tabFromUrl !== "invoice")) {
        props.setActualPage("preliquidation");
      } else {
        props.setActualPage(tabFromUrl);
      }
    }
    props.toggleRejectInvoiceModalVisibility(false);
    props.toggleModalVisibility(false);

    return () => {
      props.setDirty(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id]);

  const getPresettlement = (pageIndex: number) => {
    props.setDirty(false);
    props.setCurrentInvoice(pageIndex);
    const id = props.invoicesIds[pageIndex];

    if (props.actualPage === "preliquidation") {
      props.getDetailPreliquidation(id);
    } else {
      props.getInvoiceDetail(id);
    }

    const title = document.getElementById("invoice-number");
    const rect = title?.getBoundingClientRect();
    if (rect) {
      const scroll = window.scrollY;
      window.scroll({
        top: scroll + rect.top - 100,
        left: 0,
        behavior: "smooth",
      });
    }
  };

  const onPageClick = (pageIndex: number) => {
    const callback = () => getPresettlement(pageIndex);
    if (props.dirty) {
      props.showNotification(dirtyAlert({ onCancel: callback }));
    } else {
      callback();
    }
  };

  const isBulkFlow = useMemo(() => {
    return params.id === BULK_FLOW_ID && props?.invoicesIds?.length > 0;
  }, [props?.invoicesIds?.length, params.id]);

  return (
    <WrapperPreliquidation
      {...props}
      actualPage={props.actualPage}
      isBulkFlow={isBulkFlow}
      onPageClick={onPageClick}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  actualPage: preliquidationSelector(state).actualPage,
  showModal: preliquidationSelector(state).showRejectInvoiceModal,
  invoicesIds: preliquidationSelector(state).pendingApprovalPresettlements,
  currentInvoice: preliquidationSelector(state).currentInvoice,
  dirty: sessionSelector(state).dirtyForm,
  countryCode: sessionSelector(state).countryCode || DEFAULT_COUNTRY,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  setActualPage: (page: PagesPreliquidationTypes) => {
    dispatch(actions.setActualPage(page));
  },
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  toggleModalVisibility: (visibility: boolean) => {
    dispatch(actions.toggleModalVisibility(visibility));
  },
  toggleRejectInvoiceModalVisibility: (visibility: boolean) => {
    dispatch(actions.toggleRejectInvoiceModalVisibility(visibility));
  },
  setCurrentInvoice: (invoice: number) => {
    dispatch(actions.setCurrentInvoice(invoice));
  },
  getDetailPreliquidation: (id: number) => {
    dispatch(actions.getDetailPreliquidationsRequest(id));
  },
  getInvoiceDetail: (id: number) => {
    dispatch(actions.getInvoiceDetailRequest(id?.toString()));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(WrapperPreliquidationContainer);
